import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

export type AiProductFeedback = {
  id: number;
  work_id: number;
  product_type: string;
  created_at: string;
  updated_at: string;
  rating: number;
  response: Record<string, boolean>;
  comment: string;
  visibility: string;
  completed_at: string;
};

export const getOrCreateAiProductFeedback = async (
  workId: number,
  productType: string,
): Promise<{ ai_product_feedback: AiProductFeedback } | null> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_product_feedback_get_or_create_product_feedback_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ work_id: workId, product_type: productType }),
    },
  );
  return response.json();
};

export const updateAiProductFeedback = async ({
  id,
  rating,
  response,
  comment,
  visibility,
  completed_at,
}: {
  id: number;
  rating?: number;
  response?: Record<string, boolean>;
  comment?: string;
  visibility?: string;
  completed_at?: number;
}): Promise<{ ai_product_feedback: AiProductFeedback } | null> => {
  const apiResponse = await fetchWithCsrf(
    routes.api_v0_ai_product_feedback_update_product_feedback_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'PATCH',
      body: JSON.stringify({
        id,
        rating,
        response,
        comment,
        visibility,
        completed_at,
      }),
    },
  );
  return apiResponse.json();
};

type GetPodcastEpisodeOptions = {
  scripter?: string;
};

export const getPodcastEpisode = async (
  workId: number,
  options: GetPodcastEpisodeOptions = {},
) => {
  const { scripter } = options;
  const pathParams: Record<string, unknown> = {
    subdomain_param: 'api',
    work_id: workId,
  };

  if (scripter) {
    pathParams.scripter = scripter;
  }

  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_get_podcast_episode_path(
      pathParams as Parameters<
        typeof routes.api_v0_ai_podcast_get_podcast_episode_path
      >[0],
    ),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'GET',
    },
  );
  return response.json();
};

export const createPodcastEpisode = async (workId: number) => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_create_podcast_episode_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ work_id: workId }),
    },
  );
  return response.json();
};

export const generatePodcastEpisode = async (id: number) => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_generate_podcast_episode_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ id }),
    },
  );
  return response.json();
};

export const continueGeneratePodcastEpisode = async (id: number) => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_continue_generate_podcast_episode_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ id }),
    },
  );
  return response.json();
};

export const retryGeneratePodcastEpisode = async (
  id: number,
): Promise<{ success: boolean; error?: string }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_retry_generate_podcast_episode_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ id }),
    },
  );
  return response.json();
};

export const requestPodcast = async (workId: number) => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_request_podcast_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ work_id: workId }),
    },
  );
  return response.json();
};

export const updatePodcastEpisode = async (id: number, visibility: string) => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_update_podcast_episode_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'PATCH',
      body: JSON.stringify({ id, visibility }),
    },
  );
  return response.json();
};

export const updatePodcastSpotifyVisibility = async (
  id: number,
  spotifyVisibility: boolean,
): Promise<{ success: boolean; podcast_episode?: unknown; error?: string }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_update_spotify_visibility_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'PATCH',
      body: JSON.stringify({ id, spotify_visibility: spotifyVisibility }),
    },
  );
  return response.json();
};

export const recordPodcastListenSession = async (
  workId: number,
  podcastEpisodeId: number,
  listenSessionId: number,
  segments: { start: number; end: number; duration: number }[],
  listenDurationSeconds: number,
  source?: string,
) => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_record_podcast_listen_session_path({
      subdomain_param: 'api',
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({
        work_id: workId,
        podcast_episode_id: podcastEpisodeId,
        listen_session_id: listenSessionId,
        segments,
        listen_duration_seconds: listenDurationSeconds,
        source: source,
      }),
    },
  );
  return response.json();
};

type DownloadPodcastEpisodeOptions = {
  scripter?: string;
};

export const downloadPodcastEpisode = async (
  workId: number,
  options: DownloadPodcastEpisodeOptions = {},
): Promise<{
  success: boolean;
  download_url?: string;
  expires_at?: string;
  error?: string;
}> => {
  const { scripter } = options;
  const pathParams: Record<string, unknown> = {
    subdomain_param: 'api',
    work_id: workId,
  };

  if (scripter) {
    pathParams.scripter = scripter;
  }

  const response = await fetchWithCsrf(
    routes.api_v0_ai_podcast_download_podcast_episode_path(
      pathParams as Parameters<
        typeof routes.api_v0_ai_podcast_download_podcast_episode_path
      >[0],
    ),
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'GET',
    },
  );
  return response.json();
};
